import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2, Copy, Send, RefreshCcw, KeyRound, Power, PowerOff,
  History, ShieldCheck, AlertTriangle,
} from 'lucide-react';

// ── Status badge color map ──────────────────────────────────
const BADGE_STYLE = {
  Active: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  Inactive: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  'Activation Pending': 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  'Invite Expired': 'bg-orange-500/15 text-orange-300 border-orange-500/30',
  Invited: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  Revoked: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
};

const PRETTY = (key) => key.split('_').map(w => w[0]?.toUpperCase() + w.slice(1)).join(' ');

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

export default function ManageAccountDialog({ user, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null); // identifier for the action in flight
  const [activationLink, setActivationLink] = useState(null);
  const [resetLink, setResetLink] = useState(null);

  const load = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/users/${user.id}/account-management`);
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load account details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.id]);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch { toast.error('Could not copy. Please copy manually.'); }
  };

  const generateActivationLink = async () => {
    setBusy('activation');
    try {
      const r = await axios.post(`${API}/auth/resend-invite/${user.id}`, { send_email: false });
      setActivationLink(r.data.invite_link);
      toast.success('New activation link generated');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unable to generate activation link');
    } finally { setBusy(null); }
  };

  const resendInviteEmail = async () => {
    setBusy('resend-email');
    try {
      const r = await axios.post(`${API}/auth/resend-invite/${user.id}`, { send_email: true });
      setActivationLink(r.data.invite_link);
      if (r.data.email_sent) toast.success('Invite email resent successfully');
      else toast.message('Activation link refreshed (email delivery not configured).');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unable to resend invite email');
    } finally { setBusy(null); }
  };

  const generateResetLink = async () => {
    setBusy('reset-link');
    try {
      const r = await axios.post(`${API}/users/${user.id}/generate-reset-link`);
      setResetLink(r.data.reset_link);
      toast.success('Password reset link generated');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unable to generate reset link');
    } finally { setBusy(null); }
  };

  const sendResetEmail = async () => {
    setBusy('reset-email');
    try {
      const r = await axios.post(`${API}/users/${user.id}/send-reset`);
      if (r.data.email_sent) toast.success('Password reset email sent');
      else toast.message('Reset link generated (email delivery not configured).');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unable to send reset email');
    } finally { setBusy(null); }
  };

  const deactivate = async () => {
    if (!confirm('Deactivate this team member? They will no longer be able to log in, but their historical records will remain preserved.')) return;
    setBusy('deactivate');
    try {
      await axios.patch(`${API}/users/${user.id}/disable`);
      toast.success('Team member account deactivated');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unable to deactivate account');
    } finally { setBusy(null); }
  };

  const reactivate = async () => {
    setBusy('reactivate');
    try {
      await axios.patch(`${API}/users/${user.id}/enable`);
      toast.success('Team member account reactivated');
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unable to reactivate account');
    } finally { setBusy(null); }
  };

  const isInvited = data?.account_status === 'invited';
  const showResetActions = data?.has_password && data?.is_active;
  const badgeClass = BADGE_STYLE[data?.status_badge] || BADGE_STYLE.Invited;

  return (
    <Dialog open={!!user} onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
      <DialogContent className="bg-[#1a1d23] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="manage-account-dialog">
        <DialogHeader>
          <DialogTitle>Manage Team Member Account</DialogTitle>
        </DialogHeader>

        {loading && !data ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-white/40" /></div>
        ) : !data ? (
          <div className="py-6 text-sm text-rose-300">Account details unavailable.</div>
        ) : (
          <div className="space-y-5">
            {/* Account Overview */}
            <section data-testid="manage-account-overview">
              <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-wider text-white/40">
                <ShieldCheck className="w-3.5 h-3.5" /> Account Overview
              </div>
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="text-white/40">Full Name</span><div className="text-white">{data.name || '—'}</div></div>
                <div><span className="text-white/40">Email</span><div className="text-white break-all">{data.email}</div></div>
                <div><span className="text-white/40">Role</span><div className="text-white">{PRETTY(data.role || '—')}</div></div>
                <div><span className="text-white/40">Scope</span><div className="text-white">{data.tenant_scope?.type || 'global'}{data.tenant_scope?.centre_id ? ` · ${data.tenant_scope.centre_id}` : data.tenant_scope?.city_id ? ` · ${data.tenant_scope.city_id}` : ''}</div></div>
                <div><span className="text-white/40">Status</span><div><Badge className={`${badgeClass} border text-[10px] uppercase tracking-wider`} data-testid="manage-account-status-badge">{data.status_badge}</Badge></div></div>
                <div><span className="text-white/40">Last Login</span><div className="text-white">{fmt(data.last_login_at)}</div></div>
                <div><span className="text-white/40">Created</span><div className="text-white">{fmt(data.created_at)}</div></div>
                <div><span className="text-white/40">Updated</span><div className="text-white">{fmt(data.updated_at)}</div></div>
              </div>
            </section>

            {/* Activation & Registration */}
            {isInvited && (
              <section data-testid="manage-account-activation">
                <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-wider text-white/40">
                  <RefreshCcw className="w-3.5 h-3.5" /> Activation & Registration
                </div>
                <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 space-y-2">
                  <div className="text-xs text-white/60">
                    {data.has_active_invite && !data.invite_expired
                      ? <>Activation pending. Token expires {fmt(data.invite_expires_at)}.</>
                      : <>No active invite token. Generate a new one or resend the email.</>}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      onClick={generateActivationLink}
                      disabled={!!busy}
                      data-testid="manage-account-generate-activation-btn"
                      className="bg-amber-700/30 text-amber-200 border border-amber-500/30 hover:bg-amber-700/40 h-8 text-[11px]"
                    >
                      {busy === 'activation' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCcw className="w-3 h-3 mr-1" />}
                      {data.has_active_invite ? 'Generate New Activation Link' : 'Generate Activation Link'}
                    </Button>
                    <Button
                      size="sm"
                      onClick={resendInviteEmail}
                      disabled={!!busy}
                      data-testid="manage-account-resend-invite-btn"
                      className="bg-blue-700/30 text-blue-200 border border-blue-500/30 hover:bg-blue-700/40 h-8 text-[11px]"
                    >
                      {busy === 'resend-email' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                      Resend Invite Email
                    </Button>
                  </div>
                  {activationLink && (
                    <div className="mt-2 p-2 rounded-md bg-black/40 border border-white/10 flex items-center gap-2" data-testid="manage-account-activation-link-row">
                      <code className="flex-1 text-[10px] text-emerald-300 truncate select-all">{activationLink}</code>
                      <Button size="sm" onClick={() => copy(activationLink, 'Activation link')} className="h-6 px-2 text-[10px] bg-slate-800 border border-slate-600 text-slate-100" data-testid="manage-account-copy-activation-btn">
                        <Copy className="w-3 h-3 mr-1" /> Copy
                      </Button>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Password Access */}
            {showResetActions && (
              <section data-testid="manage-account-password-access">
                <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-wider text-white/40">
                  <KeyRound className="w-3.5 h-3.5" /> Password Access
                </div>
                <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 space-y-2">
                  <div className="text-xs text-white/60">
                    Generate a one-hour password reset link or trigger the reset email. The user&rsquo;s current password is never read or changed by these actions.
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      size="sm"
                      onClick={generateResetLink}
                      disabled={!!busy}
                      data-testid="manage-account-generate-reset-btn"
                      className="bg-violet-700/30 text-violet-200 border border-violet-500/30 hover:bg-violet-700/40 h-8 text-[11px]"
                    >
                      {busy === 'reset-link' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <KeyRound className="w-3 h-3 mr-1" />}
                      Generate Password Reset Link
                    </Button>
                    <Button
                      size="sm"
                      onClick={sendResetEmail}
                      disabled={!!busy}
                      data-testid="manage-account-send-reset-email-btn"
                      className="bg-blue-700/30 text-blue-200 border border-blue-500/30 hover:bg-blue-700/40 h-8 text-[11px]"
                    >
                      {busy === 'reset-email' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                      Send Password Reset Email
                    </Button>
                  </div>
                  {resetLink && (
                    <div className="mt-2 p-2 rounded-md bg-black/40 border border-white/10 flex items-center gap-2" data-testid="manage-account-reset-link-row">
                      <code className="flex-1 text-[10px] text-violet-300 truncate select-all">{resetLink}</code>
                      <Button size="sm" onClick={() => copy(resetLink, 'Password reset link')} className="h-6 px-2 text-[10px] bg-slate-800 border border-slate-600 text-slate-100" data-testid="manage-account-copy-reset-btn">
                        <Copy className="w-3 h-3 mr-1" /> Copy
                      </Button>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Account Status (deactivate / reactivate) */}
            <section data-testid="manage-account-status-section">
              <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-wider text-white/40">
                <Power className="w-3.5 h-3.5" /> Account Status
              </div>
              <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 space-y-2">
                {data.is_active ? (
                  <Button
                    size="sm"
                    onClick={deactivate}
                    disabled={!!busy || data.role === 'admin'}
                    data-testid="manage-account-deactivate-btn"
                    className="bg-rose-700/30 text-rose-200 border border-rose-500/30 hover:bg-rose-700/40 h-8 text-[11px]"
                  >
                    {busy === 'deactivate' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <PowerOff className="w-3 h-3 mr-1" />}
                    Deactivate Account
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={reactivate}
                    disabled={!!busy}
                    data-testid="manage-account-reactivate-btn"
                    className="bg-emerald-700/30 text-emerald-200 border border-emerald-500/30 hover:bg-emerald-700/40 h-8 text-[11px]"
                  >
                    {busy === 'reactivate' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Power className="w-3 h-3 mr-1" />}
                    Reactivate Account
                  </Button>
                )}
                {data.role === 'admin' && data.is_active && (
                  <div className="text-[10px] text-amber-300/80 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Admin accounts cannot be deactivated.
                  </div>
                )}
                {data.disabled_at && (
                  <div className="text-[10px] text-white/40">
                    Last disabled: {fmt(data.disabled_at)}
                  </div>
                )}
              </div>
            </section>

            {/* Recent account events */}
            {data.events && data.events.length > 0 && (
              <section data-testid="manage-account-events">
                <div className="flex items-center gap-2 mb-2 text-[11px] uppercase tracking-wider text-white/40">
                  <History className="w-3.5 h-3.5" /> Recent Account Events
                </div>
                <ul className="space-y-1">
                  {data.events.map((e, i) => (
                    <li key={i} className="text-[11px] text-white/60 flex flex-wrap gap-x-2">
                      <span className="text-white/40">{fmt(e.timestamp)}</span>
                      <code className="text-emerald-300/80">{e.action}</code>
                      <span className="text-white/40">·</span>
                      <span className="text-white/60">{e.actor_role}</span>
                      <span className="text-white/40 truncate max-w-full">— {e.description}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={!!busy} className="border-white/10 text-white/60">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
